import React, { useEffect, useMemo, useRef, useState } from 'react';

import _ from 'lodash';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';

import { facilityDeleteByIds } from 'api/endpoints/facility';
import Button from 'components/Button';
import Table, { IndeterminateCheckbox } from 'components/Table';
import { ENUM_ROLE_USER } from 'constants/enum';
import { FORMAT_TIME } from 'constants/index';
import { useClickOutside } from 'hooks/useClickOutside';
import { toastSuccess } from 'lib/toast';
import { clearModal, setLoading, setModal } from 'store/actions/common';
import { setModalFacility } from 'store/actions/facility';

const ActionItem = ({ item, onEdit, onDelete }) => {
    const { me } = useSelector((state) => state.auth);
    const [openControlItem, setIsOpenControlItem] = useState(false);
    const controlItemRef = useRef(null);

    useClickOutside(controlItemRef, () => setIsOpenControlItem(false));

    return (
        <div className="flex-center-center text-sm">
            <div className="relative flex justify-center items-center">
                <button type="button" className="w-6 h-8" onClick={() => setIsOpenControlItem((prev) => !prev)} ref={controlItemRef}>
                    <img src={require('assets/resources/MoreVert.svg').default} alt="more vert icon" width={24} height={24} />
                </button>
                <div className={`absolute z-2 right-0 py-1 bg-white rounded shadow-soft-md animation-dropdown ${openControlItem ? 'show' : ''}`}>
                    <ul>
                        <li
                            className="px-3 w-40 py-1.25 text-black-102 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer"
                            onClick={() => onEdit(item.id)}
                        >
                            編集
                        </li>
                        {me?.role === ENUM_ROLE_USER.ADMIN && (
                            <>
                                <div className="w-full h-[1px] bg-gray-300"></div>
                                <li
                                    onClick={() => onDelete({ show: true, item: item })}
                                    className="px-3 w-40 py-1.25 text-red-600 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer"
                                >
                                    削除
                                </li>
                            </>
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
};

// Switch Component
const StatusItem = ({ isActive }) => {
    return (
        <div
            className={`items-center text-center cursor-pointer max-w-[80px] px-2 py-1.5 rounded-full ${isActive ? 'bg-green-50' : 'bg-gray-50'
                }`}
        >
            {isActive ? <span className='text-green-600 '>● 有効</span> : <span className='text-gray-500'>● 無効 </span>}
        </div>
    );
};

const FacilityTable = (props) => {
    const dispatch = useDispatch();
    const { me } = useSelector((state) => state.auth);
    const { data, setSort, sort, page, keyword, isFilter, reloadData } = props;
    const [selectedRows, setSelectedRows] = useState([{ page: page, selected: [] }]);

    const handleRowSelect = (rowSelect) => {
        try {
            setSelectedRows((prevSelected) => {
                // Get item index with page number
                const pageSelectedExists = _.findIndex(selectedRows, { page: page });
                const selectedRowWithPage = selectedRows.concat();

                // Create new object and push to array
                if (pageSelectedExists === -1) {
                    return [
                        ...prevSelected,
                        {
                            page: page,
                            selected: [rowSelect?.original?.id],
                        },
                    ];
                }

                const rowIsSelected = selectedRowWithPage[pageSelectedExists]?.selected?.find((row) => row === rowSelect?.original?.id);

                /**
                 * If you are select
                 * - Delete the selected item from the array
                 * - Delete all Object contains lines
                 * - Add new object
                 */
                if (rowIsSelected) {
                    const selectedRemovedRow = selectedRowWithPage[pageSelectedExists].selected.filter((row) => row !== rowSelect.original.id);
                    selectedRowWithPage.splice(pageSelectedExists, 1);
                    selectedRowWithPage.push({
                        page: page,
                        selected: selectedRemovedRow,
                    });
                    return selectedRowWithPage;
                }

                const rowSelected = selectedRowWithPage[pageSelectedExists].selected;
                selectedRowWithPage.splice(pageSelectedExists, 1);
                selectedRowWithPage.push({
                    page: page,
                    selected: [...rowSelected, rowSelect.original.id],
                });
                return selectedRowWithPage;
            });
            rowSelect.getToggleSelectedHandler();
        } catch {
            // Handle error select
        }
    };

    const handleSelectAll = (table) => {
        try {
            setSelectedRows((prevState) => {
                const data = table.getRowModel().rows;
                const pageSelectedExists = _.findIndex(selectedRows, { page: page });
                const selectedRowWithPage = selectedRows.concat();

                if (pageSelectedExists === -1) {
                    return [
                        ...prevState,
                        {
                            page: page,
                            selected: data?.map((row) => row?.original?.id),
                        },
                    ];
                }

                /**
                 * If the Select of the page is currently in the Testhall (Condition) state
                 * (Condition): Select length is equal to the number of non -public status items on the current page
                 * then remove the whole object of that page to become Uncheck.
                 */
                if (selectedRowWithPage[pageSelectedExists]?.selected?.length === data?.length) {
                    selectedRowWithPage.splice(pageSelectedExists, 1);
                    return selectedRowWithPage;
                }

                selectedRowWithPage.splice(pageSelectedExists, 1);
                selectedRowWithPage.push({
                    page: page,
                    selected: data.map((row) => row?.original?.id),
                });

                return selectedRowWithPage;
            });
        } catch {
            // Handle error select all
        }
    };

    const handleDeleteByIds = (item, idFacility, isMultiple = false) => {
        const confirmDelete = async () => {
            try {
                dispatch(setLoading({ isOpen: true }));
                facilityDeleteByIds(idFacility)
                    .then(async () => {
                        await reloadData();
                        dispatch(clearModal());
                        dispatch(setLoading({ isOpen: false }));
                        if (isMultiple) {
                            toastSuccess('選択されたファイルは正常に削除されました。');
                            setSelectedRows([]);
                        } else {
                            toastSuccess('削除が完了しました。');
                            setSelectedRows(() => {
                                const selectedRowWithPage = selectedRows.concat();

                                const pageSelectedExists = _.findIndex(selectedRowWithPage, { page: page });

                                if (pageSelectedExists !== -1) {
                                    const selectedRemovedRow = selectedRowWithPage[pageSelectedExists]?.selected?.filter((row) => row !== idFacility[0]);
                                    selectedRowWithPage?.splice(pageSelectedExists, 1);

                                    selectedRowWithPage?.push({
                                        page: page,
                                        selected: selectedRemovedRow,
                                    });
                                    return selectedRowWithPage;
                                }
                                return [];
                            });
                        }
                    })
                    .catch((errors) => {
                        dispatch(clearModal());
                    }).finally(() => {
                        reloadData();
                        dispatch(setLoading({ isOpen: false }));
                    });
            } catch (error) {
                // Handle Error
                dispatch(setLoading({ isOpen: false }));
            }
        };

        dispatch(
            setModal({
                isOpen: true,
                type: 'delete',
                title: isMultiple
                    ? '本当に削除してもよろしいですか？<br />※削除後は元に戻すことはできません。'
                    : `施設「${item?.item?.name}」を削除しますか？`,
                confirmText: 'はい',
                cancelText: 'キャンセル',
                onConfirm: confirmDelete,
                onCancel: () => {
                    dispatch(clearModal());
                },
            })
        );
    };

    const onClickDeleteItems = async () => {
        const listItemsDelete = _.flatMap(selectedRows, 'selected');
        await handleDeleteByIds([], listItemsDelete, true);
    };

    const onClickDeleteItem = async (item) => {
        await handleDeleteByIds(item, [item.item.id], false);
    };

    const onClickEditFacility = async (idFacility) => {
        dispatch(setModalFacility({ isOpen: true, isEdit: true, id: idFacility }));
    };

    const columns = useMemo(
        () => [
            {
                accessorKey: '-',
                header: ({ table }) => (
                    <div className={`${data?.length <= 0 && '!cursor-not-allowed'} flex justify-center items-center w-4`}>
                        <IndeterminateCheckbox
                            className={`m-auto ${data?.length <= 0 && '!cursor-not-allowed'}`}
                            {...{
                                checked: _.find(selectedRows, { page: page })?.selected?.length === table.getRowModel().rows?.length && data?.length > 0,
                                indeterminate: false,
                                onChange: () => handleSelectAll(table),
                                disabled: me?.role !== ENUM_ROLE_USER.ADMIN,
                            }}
                        />
                    </div>
                ),
                size: 48,
                enableSorting: false,
                cell: ({ row }) => {
                    return (
                        <div className="flex justify-start">
                            <IndeterminateCheckbox
                                className={'m-auto'}
                                {...{
                                    checked: _.find(selectedRows, { page: page })?.selected?.some((item) => item === row?.original?.id),
                                    indeterminate: false,
                                    onChange: () => handleRowSelect(row),
                                    disabled: me?.role !== ENUM_ROLE_USER.ADMIN,
                                }}
                            />
                        </div>
                    );
                },
            },
            {
                accessorKey: 'id',
                header: ({ table }) => {
                    setSort(table.getState().sorting);
                    return <span>ID</span>;
                },
                cell: ({ row, getValue }) => {
                    const id = row?.original?.id || getValue();
                    return (
                        <div className="flex-between-center text-sm gap-5">
                            <span className={'break-all'}>{id}</span>
                        </div>
                    );
                },
                size: 80,
                sortingFn: () => { },
            },
            {
                accessorKey: 'name',
                enableSorting: false,
                header: () => <span>施設名称</span>,
                cell: ({ row }) => {
                    const name = row?.original?.name || '-';
                    return (
                        <div className="flex-between-center text-sm gap-5">
                            <span className={'break-all'}>{name}</span>
                        </div>
                    );
                },
                size: 250,
            },
            {
                accessorKey: 'code',
                header: () => <span>施設コード</span>,
                cell: ({ row, getValue }) => {
                    const code = row?.original?.code || getValue() || '-';
                    return (
                        <div className="flex-between-center text-sm gap-5 Break-all">
                            <span className="break-all bg-gray-100 rounded px-1.5 py-1">{code}</span>
                        </div>
                    );
                },
                size: 200,
                enableSorting: false,
            },
            {
                accessorKey: 'tel_no',
                enableSorting: false,
                header: () => <span>電話番号</span>,
                cell: ({ row }) => {
                    const telNo = row?.original?.tel_no || '-';
                    return (
                        <div className="text-sm gap-5 list-disc">
                            <span className="break-all">{telNo}</span>
                        </div>
                    );
                },
                size: 200,
            },
            {
                accessorKey: 'manager_name',
                header: () => <span>施設長名</span>,
                enableSorting: false,
                size: 150,
                cell: ({ row }) => {
                    const manageName = row?.original?.manager_name || '-';
                    return (
                        <div className="flex-between-center text-sm gap-5">
                            <span className="break-all">{manageName}</span>
                        </div>
                    );
                },
            },
            {
                accessorKey: 'is_active',
                header: () => <span>状態</span>,
                enableSorting: false,
                size: 150,
                cell: ({ row }) => (
                    <StatusItem
                        isActive={row?.original.is_active}
                    />
                ),
            },
            {
                accessorKey: 'created_at',
                header: () => <span>登録日</span>,
                enableSorting: false,
                size: 150,
                cell: ({ row }) => {
                    const createdAt = moment(row.original.created_at).format(FORMAT_TIME.DATE);
                    return (
                        <div className="flex-between-center text-sm gap-5">
                            <span className="break-all">{createdAt}</span>
                        </div>
                    );
                },
            },
            {
                accessorKey: '-',
                header: () => <span></span>,
                enableSorting: false,
                size: 88,
                cell: ({ row }) => (
                    <ActionItem item={row?.original || {}} onEdit={onClickEditFacility} onDelete={onClickDeleteItem} />
                ),
            },
        ],
        [selectedRows, page]
    );

    useEffect(() => {
        setSelectedRows([]);
    }, [sort, keyword]);

    return (
        <>
            {_.flatMap(selectedRows, 'selected').length > 0 && (
                <>
                    <div className="p-4 w-full border-2 border-solid border-primary-423 rounded-md mb-2 flex justify-between items-center h-16">
                        <p className="text-secondary-354 text-sm">{_.flatMap(selectedRows, 'selected').length} 件選択中</p>
                        <div className="flex">
                            <div className="flex gap-4">
                                <Button
                                    className="text-sm w-21 h-8"
                                    color="danger-outline"
                                    text={'削除'}
                                    onClick={onClickDeleteItems}
                                    icon={<img className="mr-2.5" src={require('assets/resources/Trash.svg').default} alt="trash icon" width={14} height={14} />}
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}
            <Table columns={columns} data={data} isFilter={isFilter} isSampleVideoList={true} />
        </>
    );
};

export default React.memo(FacilityTable);
